/**
 * Course Materials Utilities
 * Collection of utility functions and helpers
 */
class CourseMaterialsUtils {
  constructor() {
    // Static utility class - no constructor logic needed
  }

  /**
   * Count enabled campuses for the store (excludes disabled_flag).
   * @param {Array<{ disabled_flag?: boolean }>} campusEntities
   * @returns {number}
   */
  static getEnabledCampusCount(campusEntities) {
    if (!Array.isArray(campusEntities)) return 0;
    return campusEntities.filter(campus => !campus?.disabled_flag).length;
  }

  /**
   * Whether the store has only one selectable campus (CMRP hides the campus picker).
   * @param {Array<{ disabled_flag?: boolean }>} campusEntities
   * @returns {boolean}
   */
  static isSingleCampusStore(campusEntities) {
    return this.getEnabledCampusCount(campusEntities) === 1;
  }

  /**
   * Campus-term bar title: "Campus | Term", or term only when there is a single campus.
   * @param {string} campus
   * @param {string} term
   * @param {Array<{ disabled_flag?: boolean }>} [campusEntities]
   * @returns {string}
   */
  static formatCampusTermTitle(campus, term, campusEntities) {
    const campusText = String(campus || '').trim();
    const termText = String(term || '').trim();
    if (!campusText && !termText) return '';
    if (!termText) return campusText;
    if (this.isSingleCampusStore(campusEntities)) return termText;
    if (!campusText) return termText;
    return `${campusText} | ${termText}`;
  }

  /**
   * Whether the results bar should show the campus row (pin + name).
   * Hidden for single-campus stores (campus picker is also hidden).
   * @param {string} campus
   * @param {Array<{ disabled_flag?: boolean }>} [campusEntities]
   * @returns {boolean}
   */
  static shouldShowCampusInHeading(campus, campusEntities) {
    const campusText = String(campus || '').trim();
    if (!campusText) return false;
    return !this.isSingleCampusStore(campusEntities);
  }

  /**
   * Rebuild term + pin + campus markup if a caller wiped it with textContent.
   * @param {HTMLElement|null} container
   * @returns {void}
   */
  static ensureCampusTermHeadingMarkup(container) {
    if (!container) return;
    if (container.querySelector('.campus-term-heading__term')) return;

    const source = document.getElementById('selected-campus-display');
    if (
      source &&
      source !== container &&
      source.querySelector('.campus-term-heading__term')
    ) {
      container.classList.add('campus-term-heading');
      container.innerHTML = source.innerHTML;
      return;
    }

    container.classList.add('campus-term-heading');
    container.innerHTML = `
      <span class="campus-term-heading__term"></span>
      <span class="campus-term-heading__campus" hidden>
        <span class="campus-term-heading__pin" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 12 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6 3C4.55025 3 3.375 4.17525 3.375 5.625C3.375 7.07475 4.55025 8.25 6 8.25C7.44975 8.25 8.625 7.07475 8.625 5.625C8.625 4.17525 7.44975 3 6 3ZM4.875 5.625C4.875 5.00368 5.37868 4.5 6 4.5C6.62132 4.5 7.125 5.00368 7.125 5.625C7.125 6.24632 6.62132 6.75 6 6.75C5.37868 6.75 4.875 6.24632 4.875 5.625Z" fill="currentColor"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.56709 14.8624L5.56898 14.8638C5.69827 14.9544 5.84914 14.9998 6 14.9999C6.15073 14.9999 6.30145 14.9546 6.43063 14.864L6.43153 14.8634L6.43291 14.8624L6.4371 14.8595L6.45107 14.8495C6.46284 14.8411 6.47946 14.8292 6.5006 14.8138C6.54289 14.783 6.60332 14.7383 6.6793 14.6806C6.8312 14.5652 7.04568 14.3972 7.30192 14.1825C7.81329 13.7542 8.49667 13.1354 9.18231 12.3741C10.5272 10.8807 12 8.7079 12 6.27273C12 4.62101 11.3766 3.02977 10.2559 1.85083C9.13394 0.6706 7.60391 0 6 0C4.39609 0 2.86606 0.6706 1.74411 1.85083C0.623386 3.02977 0 4.62101 0 6.27273C0 8.7079 1.47276 10.8807 2.81769 12.3741C3.50333 13.1354 4.18671 13.7542 4.69808 14.1825C4.95432 14.3972 5.1688 14.5652 5.3207 14.6806C5.39668 14.7383 5.45711 14.783 5.4994 14.8138C5.52054 14.8292 5.53716 14.8411 5.54893 14.8495L5.5629 14.8595L5.56709 14.8624ZM2.83127 2.88431C3.67846 1.99311 4.81913 1.5 6 1.5C7.18087 1.5 8.32154 1.99311 9.16873 2.88431C10.0171 3.77679 10.5 4.99501 10.5 6.27273C10.5 8.13301 9.34776 9.94888 8.06769 11.3703C7.44083 12.0663 6.81171 12.6364 6.33871 13.0327C6.21345 13.1376 6.09954 13.23 6 13.3089C5.90046 13.23 5.78655 13.1376 5.66129 13.0327C5.18829 12.6364 4.55917 12.0663 3.93231 11.3703C2.65224 9.94888 1.5 8.13301 1.5 6.27273C1.5 4.99501 1.98286 3.77679 2.83127 2.88431Z" fill="currentColor"/>
          </svg>
        </span>
        <span class="campus-term-heading__campus-name"></span>
      </span>
    `;
  }

  /**
   * Populate structured campus/term heading (term + optional pin/campus).
   * @param {HTMLElement|null} container
   * @param {{ campus?: string, term?: string, campusEntities?: Array<{ disabled_flag?: boolean }>, forceCampus?: boolean }} parts
   * @returns {void}
   */
  static renderCampusTermHeading(container, parts = {}) {
    if (!container) return;

    this.ensureCampusTermHeadingMarkup(container);

    const campusText = String(parts.campus || '').trim();
    const termText = String(parts.term || '').trim();
    const campusEntities = parts.campusEntities;
    const termEl = container.querySelector('.campus-term-heading__term');
    const campusEl = container.querySelector('.campus-term-heading__campus');
    const campusNameEl = container.querySelector('.campus-term-heading__campus-name');

    if (termEl) {
      termEl.textContent = termText;
    }

    const showCampus =
      parts.forceCampus === true
        ? Boolean(campusText)
        : this.shouldShowCampusInHeading(campusText, campusEntities);
    if (campusNameEl) {
      campusNameEl.textContent = showCampus ? campusText : '';
    }
    if (campusEl) {
      campusEl.hidden = !showCampus;
    }

    container.dataset.campusTermTitle = this.formatCampusTermTitle(
      campusText,
      termText,
      campusEntities
    );
  }

  /**
   * Fill #selected-campus-display without wiping pin markup.
   * @param {HTMLElement|null} container
   * @param {string} campus
   * @param {string} term
   * @param {{ forceCampus?: boolean }} [options]
   * @returns {void}
   */
  static setResultsCampusTermHeading(container, campus, term, options = {}) {
    if (!container) return;
    this.renderCampusTermHeading(container, {
      campus,
      term,
      campusEntities: window.courseData?.ddcs_campus_entity,
      forceCampus: options.forceCampus === true,
    });
  }

  /**
   * Clear structured #selected-campus-display.
   * @param {HTMLElement|null} container
   * @returns {void}
   */
  static clearCampusTermHeading(container) {
    if (!container) return;
    this.renderCampusTermHeading(container, { campus: '', term: '' });
    delete container.dataset.campusTermTitle;
  }

  /**
   * Plain-text campus/term label for print / mobile headers.
   * @param {HTMLElement|null} container
   * @returns {string}
   */
  static getCampusTermHeadingText(container) {
    if (!container) return '';
    const fromData = container.dataset?.campusTermTitle?.trim();
    if (fromData) return fromData;

    const termEl = container.querySelector('.campus-term-heading__term');
    const campusNameEl = container.querySelector('.campus-term-heading__campus-name');
    if (termEl || campusNameEl) {
      const term = termEl?.textContent?.trim() || '';
      const campus = campusNameEl?.textContent?.trim() || '';
      if (campus && term) return `${campus} | ${term}`;
      return term || campus;
    }

    return container.textContent?.trim() || '';
  }

  /**
   * Format date string to readable format
   */
  static formatDate(dateString, options = {}) {
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        ...defaultOptions,
        ...options,
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format currency amount
   */
  static formatCurrency(amount, currency = 'USD') {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '$0.00';
    }

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      console.warn('Error formatting currency:', error);
      return `$${parseFloat(amount).toFixed(2)}`;
    }
  }

  /**
   * Sanitize HTML content
   */
  static sanitizeHTML(str) {
    if (typeof str !== 'string') return '';

    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Truncate text to specified length
   */
  static truncateText(text, maxLength = 100, ellipsis = '...') {
    if (typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;

    return text.substring(0, maxLength - ellipsis.length).trim() + ellipsis;
  }

  /**
   * Generate unique ID
   */
  static generateId(prefix = 'id') {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2)}`;
  }

  /**
   * Deep clone an object
   */
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => this.deepClone(item));
    if (typeof obj === 'object') {
      const cloned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = this.deepClone(obj[key]);
        }
      }
      return cloned;
    }
    return obj;
  }

  /**
   * Merge objects deeply
   */
  static deepMerge(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (this.isObject(target) && this.isObject(source)) {
      for (const key in source) {
        if (this.isObject(source[key])) {
          if (!target[key]) Object.assign(target, { [key]: {} });
          this.deepMerge(target[key], source[key]);
        } else {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }

    return this.deepMerge(target, ...sources);
  }

  /**
   * Check if value is an object
   */
  static isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Get nested object property safely
   */
  static getNestedProperty(obj, path, defaultValue = null) {
    if (!obj || typeof obj !== 'object') return defaultValue;

    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
      if (result === null || result === undefined || !(key in result)) {
        return defaultValue;
      }
      result = result[key];
    }

    return result;
  }

  /**
   * Set nested object property safely
   */
  static setNestedProperty(obj, path, value) {
    if (!obj || typeof obj !== 'object') return obj;

    const keys = path.split('.');
    const lastKey = keys.pop();
    let current = obj;

    for (const key of keys) {
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[lastKey] = value;
    return obj;
  }

  /**
   * Debounce function execution
   */
  static debounce(func, wait, immediate = false) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func.apply(this, args);
      };

      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);

      if (callNow) func.apply(this, args);
    };
  }

  /**
   * Throttle function execution
   */
  static throttle(func, limit) {
    let lastFunc;
    let lastRan;

    return function executedFunction(...args) {
      if (!lastRan) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  /**
   * Convert string to slug (URL-friendly)
   */
  static slugify(str) {
    if (typeof str !== 'string') return '';

    return str
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Capitalize first letter of each word
   */
  static titleCase(str) {
    if (typeof str !== 'string') return '';

    return str.replace(/\w\S*/g, txt => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  /**
   * Convert camelCase to kebab-case
   */
  static camelToKebab(str) {
    if (typeof str !== 'string') return '';

    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Convert kebab-case to camelCase
   */
  static kebabToCamel(str) {
    if (typeof str !== 'string') return '';

    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  /**
   * Check if element is in viewport
   */
  static isInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Smooth scroll to element
   */
  static scrollToElement(element, offset = 0, behavior = 'smooth') {
    if (!element) return;

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: behavior,
    });
  }

  /**
   * Get element's offset position relative to document
   */
  static getElementOffset(element) {
    if (!element) return { top: 0, left: 0 };

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
    };
  }

  /**
   * Create DOM element with attributes
   */
  static createElement(tag, attributes = {}, children = []) {
    const element = document.createElement(tag);

    // Set attributes
    for (const [key, value] of Object.entries(attributes)) {
      if (key === 'className') {
        element.className = value;
      } else if (key === 'textContent') {
        element.textContent = value;
      } else if (key === 'innerHTML') {
        element.innerHTML = value;
      } else {
        element.setAttribute(key, value);
      }
    }

    // Add children
    children.forEach(child => {
      if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      } else if (child instanceof Node) {
        element.appendChild(child);
      }
    });

    return element;
  }

  /**
   * Remove element safely
   */
  static removeElement(element) {
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
  }

  /**
   * Add CSS class if not present
   */
  static addClass(element, className) {
    if (element && className) {
      element.classList.add(className);
    }
  }

  /**
   * Remove CSS class if present
   */
  static removeClass(element, className) {
    if (element && className) {
      element.classList.remove(className);
    }
  }

  /**
   * Toggle CSS class
   */
  static toggleClass(element, className) {
    if (element && className) {
      element.classList.toggle(className);
    }
  }

  /**
   * Check if element has class
   */
  static hasClass(element, className) {
    return element && className && element.classList.contains(className);
  }

  /**
   * Get URL parameters as object
   */
  static getURLParams() {
    const params = {};
    const searchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  }

  /**
   * Set URL parameters
   */
  static setURLParams(params, replaceState = true) {
    const url = new URL(window.location);

    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    }

    if (replaceState) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
  }

  /**
   * Format file size
   */
  static formatFileSize(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Generate random hex color
   */
  static randomHexColor() {
    return (
      '#' +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, '0')
    );
  }

  /**
   * Check if device is mobile
   */
  static isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Check if device is iOS
   */
  static isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Get browser name
   */
  static getBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase();

    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!window.opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!window.chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  /**
   * Copy text to clipboard
   */
  static async copyToClipboard(text) {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      }
    } catch (error) {
      console.error('Failed to copy text:', error);
      return false;
    }
  }

  /**
   * Validate email address
   */
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number (US format)
   */
  static isValidPhoneUS(phone) {
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  }

  /**
   * Format phone number (US format)
   */
  static formatPhoneUS(phone) {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }

    return phone;
  }

  /**
   * Create loading spinner element
   */
  static createSpinner(size = 'medium', color = '#000') {
    const sizes = {
      small: '16px',
      medium: '24px',
      large: '32px',
    };

    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner';
    spinner.style.cssText = `
            width: ${sizes[size]};
            height: ${sizes[size]};
            border: 2px solid rgba(0,0,0,0.1);
            border-top: 2px solid ${color};
            border-radius: 50%;
            animation: spin 1s linear infinite;
            display: inline-block;
        `;

    // Add keyframes if not already added
    if (!document.getElementById('spinner-keyframes')) {
      const style = document.createElement('style');
      style.id = 'spinner-keyframes';
      style.textContent = `
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;
      document.head.appendChild(style);
    }

    return spinner;
  }

  /**
   * Log with timestamp and context
   */
  static log(message, level = 'info', context = null) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    const fullMessage = context
      ? `${prefix} ${message}`
      : `${prefix} ${message}`;

    switch (level) {
      case 'error':
        console.error(fullMessage, context);
        break;
      case 'warn':
        console.warn(fullMessage, context);
        break;
      default:
        break;
    }
  }
}

// Make the class available globally
window.CourseMaterialsUtils = CourseMaterialsUtils;
